/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type {
  XdpDataQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember_cdpMetadata_ratings as CourseRating,
  XdpDataQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_cdpMetadataMember_cdpMetadata_partners as PartnerType,
} from 'bundles/accomplishments/components/course-certificates/__generated__/XdpDataQuery';
import type { XdpSpecializationMetadataQuery_XdpV1Resource_slug_elements_xdpMetadata_XdpV1_sdpMetadataMember_sdpMetadata_ratings as S12nRating } from 'bundles/accomplishments/components/s12n-certificates/__generated__/XdpSpecializationMetadataQuery';
import CourseTotalRatings from 'bundles/accomplishments/components/shared/CourseTotalRatings';
import TryCourseButton from 'bundles/accomplishments/components/shared/TryCourseButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/accomplishments';

// These are just the styles that are used in the ProductOverviewSnippet.styl file from video-logged-out-page.
// In a future PR, we should better support CDS breakpoints and variables.
const styles = {
  productOverviewSnippet: css`
    display: flex;
    margin: 25px auto 0;
    border-top: none;
    width: 100%;
    max-width: 780px;
    justify-content: space-between;

    @media (max-width: 1199px) {
      /* preserved from stylus - $desktop-or-smaller */
      flex-direction: column;
    }

    @media (max-width: 991px) {
      /* preserved from stylus - $tablet-or-smaller */
      margin-top: 40px;
    }
  `,
  productInfoSectionContainer: css`
    padding-right: 20px;
    display: flex;
    justify-content: center;

    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      padding-right: 0;
    }
  `,
  productInfoSection: css`
    width: 100%;
    display: flex;
    align-items: flex-start;
  `,
  courseNameContainer: css`
    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  `,
  courseInfoToggle: css`
    display: none;
    padding: 0;
    margin: 0 0 0 10px;

    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      display: inline-block;
    }
  `,
  partnerLogoContainer: css`
    display: inline-block;
    height: 80px;
    width: 80px;
    padding-right: var(--cds-spacing-50);
    margin-right: var(--cds-spacing-100);

    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      height: 70px;
      width: 70px;
    }
  `,
  creatorLogo: css`
    max-width: 80px;
    max-height: 80px;

    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      height: 70px;
      width: 70px;
    }
  `,
  partnerLogo: css`
    align-items: center;
    top: 0;
    width: 140px;
  `,
  infoContent: css`
    min-height: 80px;
    display: inline-block;
    color: var(--cds-color-grey-975);
    flex: 1;
    min-width: 0;

    @media (max-width: 767px) {
      /* preserved from stylus - $phone-or-smaller */
      height: auto;
    }
  `,
  courseNameHeader: css`
    line-height: normal;
    font-weight: bold;
    text-decoration: underline;
    font-size: 18px;
    margin-bottom: 0;
  `,
  partnerDesc: css`
    font-size: 12px;
    padding-bottom: 2px;
  `,
  productInfoButtons: css`
    text-align: center;

    @media (max-width: 1199px) {
      /* preserved from stylus - $desktop-or-smaller */
      width: 100%;
      margin-top: 20px;
    }

    .try-course {
      font-weight: bold;
      font-size: 14px;
      padding: 0;
      height: 48px;
      width: 170px;
      margin: 0;

      @media (max-width: 767px) {
        /* preserved from stylus - $phone-or-smaller */
        width: 100%;
        max-width: 100%;
      }
    }
  `,
  tryButton: css`
    margin-bottom: 0;
  `,
};

type ProductOverviewSnippetProps = {
  id?: string;
  name?: string;
  productLink?: string;
  ratings?: CourseRating | S12nRating | null;
  partners?: Array<PartnerType>;
  isCertificateOwner?: boolean;
  isS12n?: boolean;
  enrollmentCount?: number;
};

type TitleHeaderProps = {
  productLink: string;
  name?: string;
};

type LogoContainerProps = {
  partner: PartnerType;
  logoSrc: string;
  productLink: string;
};

type PartnerNameProps = { productLink: string; partnerName: string };

const LOGO_HEIGHT = 80;
const LOGO_WIDTH = 80;

const LogoContainer = ({ partner, logoSrc, productLink }: LogoContainerProps) => {
  if (!partner || !productLink || !logoSrc) {
    return null;
  }

  return (
    <div className="partner-logo-container" css={styles.partnerLogoContainer}>
      <TrackedA
        trackingName="product_university_logo"
        className="nostyle"
        href={productLink}
        rel="noopener noreferrer"
        target="_blank"
        data-e2e="partner-logo-link"
      >
        <Imgix
          src={logoSrc}
          key={partner.id}
          alt={partner.name}
          maxHeight={LOGO_HEIGHT}
          maxWidth={LOGO_WIDTH}
          css={styles.creatorLogo}
        />
      </TrackedA>
    </div>
  );
};

const TitleHeader = ({ productLink, name }: TitleHeaderProps) => {
  if (!name || !productLink) {
    return null;
  }

  return (
    <div data-e2e="course-name">
      <h3 className="course-name-header" css={styles.courseNameHeader}>
        <TrackedA href={productLink} trackingName="product_link" className="nostyle">
          {name}
        </TrackedA>
      </h3>
    </div>
  );
};

const PartnerName = ({ productLink, partnerName }: PartnerNameProps) => {
  if (!partnerName || !productLink) {
    return null;
  }

  return (
    <span className="partner-desc" css={styles.partnerDesc}>
      <TrackedA href={productLink} className="nostyle" trackingName="product_university_name">
        {partnerName}
      </TrackedA>
    </span>
  );
};

const ProductOverviewSnippet = ({
  ratings,
  partners,
  productLink,
  name,
  isCertificateOwner,
  isS12n,
  enrollmentCount,
}: ProductOverviewSnippetProps) => {
  const partner = partners && partners[0];
  const { name: partnerName = '', squareLogo: partnerSquareLogo = '' } = partner || {};
  const logoSrc = partnerSquareLogo;
  const tryCourseButtonMessage = <FormattedMessage message={_t('Enroll for Free')} />;

  if (!productLink) {
    return null;
  }

  return (
    <div className="rc-ProductOverviewSnippet" css={styles.productOverviewSnippet}>
      <div
        className="product-info-section-container"
        css={[styles.productInfoSectionContainer, styles.productInfoSection]}
      >
        {partner && logoSrc && <LogoContainer partner={partner} logoSrc={logoSrc} productLink={productLink} />}
        <div css={styles.infoContent}>
          <div className="course-name-container" css={styles.courseNameContainer}>
            <TitleHeader productLink={productLink} name={name} />
          </div>
          <PartnerName productLink={productLink} partnerName={partnerName} />
          {ratings && isS12n && (
            <CourseTotalRatings
              productLink={productLink}
              courseEnrollmentCount={enrollmentCount}
              ratings={ratings}
              totalReviewCount={Number(ratings.ratingCount)}
              a11yIdentifier="ProductOverview"
            />
          )}
          {ratings && !isS12n && (
            <CourseTotalRatings
              productLink={productLink}
              courseEnrollmentCount={enrollmentCount}
              ratings={ratings}
              totalReviewCount={Number(ratings.ratingCount)}
              a11yIdentifier="ProductOverview"
            />
          )}
        </div>
      </div>
      {productLink && !isCertificateOwner && (
        <div className="product-info-buttons" css={styles.productInfoButtons}>
          <TryCourseButton
            productLink={productLink}
            buttonClass="try-course"
            buttonCss={{ '&&': { marginBottom: 0 } }}
            customMessage={tryCourseButtonMessage}
            sectionName="cta_banner"
            buttonName="enroll"
          />
        </div>
      )}
    </div>
  );
};

export default ProductOverviewSnippet;
